export const SITE_BASE_URL = "https://surensinffotek.com";

export const ORG = {
  "@type": "Organization",
  name: "SurensInffotek",
  url: SITE_BASE_URL,
  logo: `${SITE_BASE_URL}/logo.png`,
};

const truncate = (str, max) =>
  str.length <= max ? str : `${str.slice(0, max - 1).trimEnd()}…`;

function automationPhrase(uc) {
  const type = uc.automationType || "";
  if (type.includes("Agentic AI")) return "agentic AI workflow automation";
  if (type.includes("Voice AI")) return "voice AI and AI agents";
  if (type.includes("Document AI")) return "document AI and workflow automation";
  if (type.includes("Data Automation")) return "data automation and AI agents";
  return "AI automation";
}

const DOMAIN_INTEGRATIONS = {
  "Financial Domain":
    "Use cases integrate with ERP, AP/AR modules, treasury systems, tax engines, procurement portals, banking platforms, email, SharePoint, and BI tools — with configurable connectors for finance shared services in India, UAE, KSA, UK, and USA.",
  Manufacturing:
    "Use cases integrate with ERP, MES, PLM, QMS, WMS, SCADA, and BI platforms — with configurable connectors for discrete and process manufacturing deployments in India, UAE, KSA, UK, and USA.",
  "HR Domain":
    "Use cases integrate with HRMS platforms such as Workday, SAP SuccessFactors, Darwinbox, and Zoho People, along with payroll systems, email, calendar, SharePoint, and ITSM — with configurable connectors for enterprise HR deployments in India, UAE, KSA, UK, and USA.",
  BFSI:
    "Use cases integrate with core banking, LOS, LMS, CRM, AML, KYC, claims, payment switch, data warehouse, email, and contact center platforms — with configurable connectors for banking and financial services in India, UAE, KSA, UK, and USA.",
  Healthcare:
    "Use cases integrate with HIS, EMR/EHR, LIS, RIS/PACS, billing, insurance portal, pharmacy, CRM, email, and SMS/WhatsApp — with configurable connectors for hospitals and health systems in India, UAE, KSA, UK, and USA.",
  Logistics:
    "Use cases integrate with TMS, WMS, ERP, CRM, GPS/telematics, customs portal, carrier portal, email, and BI — with configurable connectors for 3PL and supply chain deployments in India, UAE, KSA, UK, and USA.",
  Retail:
    "Use cases integrate with POS, OMS, WMS, ERP, CRM, loyalty, payment gateway, Shopify, Magento, marketplace portals, email, and BI — with configurable connectors for retail and e-commerce in India, UAE, KSA, UK, and USA.",
  Insurance:
    "Use cases integrate with policy admin systems, claims platforms, CRM, document management, email, and BI — with configurable connectors for insurers and brokers in India, UAE, KSA, UK, and USA.",
  Telecom:
    "Use cases integrate with BSS/OSS, CRM, network monitoring, ITSM, field service, email, and BI — with configurable connectors for telecom operators in India, UAE, KSA, UK, and USA.",
  Automotive:
    "Use cases integrate with MES, QMS, PLM, ERP, warranty systems, dealer management, battery test systems, data lakes, and supplier portals — with configurable connectors for OEMs and EV programs in India, UAE, KSA, UK, and USA.",
  Pharma:
    "Use cases integrate with MES, LIMS, QMS, DMS, ERP, batch record systems, email, and BI — with configurable connectors for GxP-regulated pharma manufacturing in India, UAE, KSA, UK, and USA.",
  Aviation:
    "Use cases integrate with MRO systems, crew management, safety management, ERP, inventory, HRMS, and document management — with configurable connectors for airlines and MRO providers in India, UAE, KSA, UK, and USA.",
  "IT & Technology Services":
    "Use cases integrate with ITSM, DevOps, SIEM, CRM, document management, HRMS, email, and BI — with configurable connectors for IT services and product engineering teams in India, UAE, KSA, UK, and USA.",
  "Public Sector & Government":
    "Use cases integrate with case management, document management, citizen portals, CRM, email, and BI — with configurable connectors for government agencies in India, UAE, KSA, UK, and USA.",
  "Real Estate & Construction":
    "Use cases integrate with project management, ERP, document management, CRM, procurement, email, and BI — with configurable connectors for developers and contractors in India, UAE, KSA, UK, and USA.",
  "Hospitality & Travel":
    "Use cases integrate with PMS, CRS, GDS, CRM, channel managers, HRMS, email, and BI — with configurable connectors for hotels and travel operators in India, UAE, KSA, UK, and USA.",
  "Agriculture & Food Processing":
    "Use cases integrate with ERP, QMS, traceability systems, CRM, email, and BI — with configurable connectors for food brands and processors in India, UAE, KSA, UK, and USA.",
  "Media & Entertainment":
    "Use cases integrate with MAM, CMS, CRM, social platforms, email, and BI — with configurable connectors for media and entertainment companies in India, UAE, KSA, UK, and USA.",
  "Energy & Utilities":
    "Use cases integrate with SCADA, GIS, CRM, ERP, field service management, email, and BI — with configurable connectors for utilities in India, UAE, KSA, UK, and USA.",
  Education:
    "Use cases integrate with LMS, SIS, CRM, email, portal, and BI — with configurable connectors for schools, universities, and EdTech providers in India, UAE, KSA, UK, and USA.",
};

function domainAutomationSummary(useCasesSeo) {
  const types = [...new Set(useCasesSeo.map((uc) => uc.automationType).filter(Boolean))];
  if (!types.length) return "agentic AI workflow automation";
  if (types.length === 1) return types[0];
  return types.slice(0, 3).join(", ") + (types.length > 3 ? ", and related agentic AI patterns" : "");
}

export function buildDomainFaqs(domainIntro, useCasesSeo, mode = "agentic") {
  const groups = [...new Set(useCasesSeo.map((uc) => uc.subIndustry))];
  const groupPreview =
    groups.length > 6
      ? `${groups.slice(0, 6).join(", ")}, and more`
      : groups.join(", ");
  const domainName = domainIntro.title;
  const automationSummary =
    mode === "rpa"
      ? domainAutomationSummary(useCasesSeo).replace(/agentic AI/gi, "RPA")
      : domainAutomationSummary(useCasesSeo);
  const label = mode === "rpa" ? "RPA" : "agentic AI";
  const systemsAnswer =
    DOMAIN_INTEGRATIONS[domainName] ||
    (useCasesSeo[0]?.systems
      ? `Use cases integrate with ${useCasesSeo[0].systems} and related enterprise platforms — with configurable connectors for practical deployment in India, UAE, KSA, UK, and USA.`
      : "Use cases integrate with enterprise platforms relevant to the domain — including ERP, CRM, document management, email, and workflow systems — with configurable connectors for practical enterprise deployment.");

  const faqs = [
    {
      question: `What is ${domainName} ${label} automation?`,
      answer: domainIntro.content,
    },
    {
      question: `How many ${domainName} ${label} use cases are documented?`,
      answer: `${domainName} includes ${useCasesSeo.length} documented ${label} use cases across ${groups.length} process areas, including ${groupPreview}. Automation types include ${automationSummary}. Each use case includes challenge and solution detail, process diagrams, and human-in-the-loop review.`,
    },
    {
      question: `Which systems do ${domainName} automations integrate with?`,
      answer: systemsAnswer,
    },
    {
      question: `Which regions are ${domainName} solutions designed for?`,
      answer:
        "Solutions are designed for enterprises in India, UAE, KSA, UK, and USA, with configurable business rules for regional policy and compliance requirements.",
    },
    {
      question: `Do ${domainName} automations replace human reviewers?`,
      answer:
        "No. Each use case is built for practical deployment with human approval where needed. Automations reduce manual effort, improve accuracy, and maintain audit trails without removing human oversight.",
    },
  ];

  return faqs;
}

export function buildFaqJsonLdFromFaqs(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

function rpaAutomationPhrase(uc) {
  const type = uc.automationType || "";
  if (type.includes("Document AI")) return "document AI + RPA";
  if (type.includes("Workflow")) return "RPA workflow automation";
  return "RPA automation";
}

function buildUseCaseFaqs(uc, mode = "agentic") {
  const faqLabel = uc.shortTitle || uc.businessProcess || uc.seoHeadline || uc.title;
  const autoPhrase = mode === "rpa" ? rpaAutomationPhrase(uc) : automationPhrase(uc);
  const faqs = [
    {
      question: `What challenge does ${faqLabel} address in ${uc.subIndustry}?`,
      answer: uc.challenge,
    },
    {
      question: `How does ${autoPhrase} support ${faqLabel}?`,
      answer: uc.solution,
    },
  ];

  if (uc.buyerPersona) {
    faqs.push({
      question: `Who benefits from ${faqLabel}?`,
      answer: `This use case is designed for ${uc.buyerPersona} teams adopting ${autoPhrase} in ${uc.subIndustry}.`,
    });
  }

  if (uc.systems) {
    faqs.push({
      question: `Which systems integrate with ${faqLabel}?`,
      answer: `Typical integrations include ${uc.systems}, with configurable connectors for enterprise deployment.`,
    });
  }

  if (uc.regions) {
    faqs.push({
      question: `Which regions is ${faqLabel} designed for?`,
      answer: `This use case is relevant for enterprises in ${uc.regions}, with configurable business rules for local policy requirements.`,
    });
  }

  if (uc.keyBenefits) {
    faqs.push({
      question: `What are the key benefits of ${faqLabel}?`,
      answer: uc.keyBenefits,
    });
  }

  return faqs;
}

export function enrichUseCaseForSeo(uc, { domainSlug, headline, mode = "agentic", basePath = "/agentic-ai" }) {
  const seoHeadline = headline || uc.seoTitle || uc.title;
  const keywordsArray = (uc.seoKeywords || "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
  const groupId =
    uc.groupId ||
    String(uc.subIndustry || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  const canonicalPath = `${basePath}/${domainSlug}/${groupId}/${uc.id}`;
  const canonicalUrl = `${SITE_BASE_URL}${canonicalPath}`;
  const titleSuffix = mode === "rpa" ? "RPA Automation" : "Agentic AI Automation";
  const metaTitle = truncate(`${seoHeadline} | ${uc.subIndustry} ${titleSuffix}`, 60);
  const metaDescription = truncate(uc.solution, 155);
  const defaultSummaryPrefix = mode === "rpa"
    ? `${seoHeadline} is a ${uc.automationType || "RPA"} use case for ${uc.subIndustry} that`
    : `${seoHeadline} is an Agentic AI + Workflow Automation use case for ${uc.subIndustry} that`;
  const seoSummary =
    uc.summary ||
    `${defaultSummaryPrefix} ${uc.solution.charAt(0).toLowerCase()}${uc.solution.slice(1)}`;
  const faqs = buildUseCaseFaqs({ ...uc, seoHeadline }, mode);

  return {
    ...uc,
    seoHeadline,
    keywordsArray,
    canonicalPath,
    canonicalUrl,
    metaTitle,
    metaDescription,
    seoSummary,
    faqs,
  };
}

export function buildItemListJsonLd(useCases, listName) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    numberOfItems: useCases.length,
    itemListElement: useCases.map((uc, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: uc.seoHeadline || uc.title,
      url: uc.canonicalUrl,
      item: {
        "@type": "TechArticle",
        headline: uc.seoHeadline || uc.title,
        description: uc.challenge,
        articleBody: uc.solution,
        keywords: uc.keywordsArray?.join(", ") || uc.seoKeywords,
        url: uc.canonicalUrl,
        ...(uc.automationType ? { genre: uc.automationType } : {}),
      },
    })),
  };
}

export function buildFaqJsonLd(useCases) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: useCases.flatMap((uc) =>
      uc.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      }))
    ),
  };
}

export function buildBreadcrumbJsonLd(domainIntro, domainSlug) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Agentic AI",
        item: `${SITE_BASE_URL}/agentic-ai`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: domainIntro.title,
        item: `${SITE_BASE_URL}/agentic-ai/${domainSlug}`,
      },
    ],
  };
}

export function buildWebPageJsonLd(domainIntro, useCases, pageName, domainSlug) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: pageName || `${domainIntro.title} Use Cases`,
    description: domainIntro.seoDescription,
    url: domainSlug ? `${SITE_BASE_URL}/agentic-ai/${domainSlug}` : `${SITE_BASE_URL}/agentic-ai`,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "SurensInffotek",
      url: SITE_BASE_URL,
    },
    publisher: ORG,
    about: {
      "@type": "Thing",
      name: `${domainIntro.title} Agentic AI Automation`,
      description: domainIntro.content,
    },
    mainEntity: buildItemListJsonLd(useCases, pageName || `${domainIntro.title} Use Cases`),
  };
}

export function buildCombinedFaqJsonLd(domainFaqs, useCases) {
  const domainEntities = buildFaqJsonLdFromFaqs(domainFaqs).mainEntity;
  const useCaseEntities = buildFaqJsonLd(useCases).mainEntity;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [...domainEntities, ...useCaseEntities],
  };
}

export function buildUseCaseBreadcrumbJsonLd({
  mode = "agentic",
  domainSlug,
  domainIntro,
  group,
  useCase,
}) {
  const basePath = mode === "rpa" ? "/rpa-use-cases" : "/agentic-ai";
  const hubLabel = mode === "rpa" ? "RPA Use Cases" : "Agentic AI";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: hubLabel,
        item: `${SITE_BASE_URL}${basePath}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: domainIntro.title,
        item: `${SITE_BASE_URL}${basePath}/${domainSlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: group.label,
        item: `${SITE_BASE_URL}${basePath}/${domainSlug}/${group.id}`,
      },
      {
        "@type": "ListItem",
        position: 5,
        name: useCase.title,
        item: useCase.canonicalUrl,
      },
    ],
  };
}

export function buildGroupBreadcrumbJsonLd({
  mode = "agentic",
  domainSlug,
  domainIntro,
  group,
}) {
  const basePath = mode === "rpa" ? "/rpa-use-cases" : "/agentic-ai";
  const hubLabel = mode === "rpa" ? "RPA Use Cases" : "Agentic AI";
  const groupUrl = `${SITE_BASE_URL}${basePath}/${domainSlug}/${group.id}`;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: hubLabel,
        item: `${SITE_BASE_URL}${basePath}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: domainIntro.title,
        item: `${SITE_BASE_URL}${basePath}/${domainSlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: group.label,
        item: groupUrl,
      },
    ],
  };
}

export function buildUseCaseServiceJsonLd(useCase, mode = "agentic") {
  const label = mode === "rpa" ? "RPA Automation" : "Agentic AI Automation";
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${useCase.canonicalUrl}#service`,
        name: useCase.seoHeadline || useCase.title,
        description: useCase.solution,
        serviceType: `${useCase.subIndustry} ${label}`,
        areaServed: ["IN", "AE", "SA", "GB", "US"],
        url: useCase.canonicalUrl,
        provider: ORG,
        keywords: useCase.keywordsArray?.join(", ") || useCase.seoKeywords,
      },
      {
        "@type": "TechArticle",
        "@id": `${useCase.canonicalUrl}#article`,
        headline: useCase.seoHeadline || useCase.title,
        description: useCase.metaDescription || useCase.solution,
        articleBody: `${useCase.challenge} ${useCase.solution}`,
        keywords: useCase.keywordsArray?.join(", ") || useCase.seoKeywords,
        url: useCase.canonicalUrl,
        author: ORG,
        publisher: ORG,
        mainEntityOfPage: useCase.canonicalUrl,
        about: {
          "@type": "Thing",
          name: `${useCase.subIndustry} ${label}`,
        },
      },
    ],
  };
}

export function buildDomainSeoBundle({
  domainSlug,
  domainIntro,
  useCases,
  getHeadline = (uc) => uc.seoTitle || uc.title,
}) {
  const useCasesSeo = useCases.map((uc) =>
    enrichUseCaseForSeo(uc, { domainSlug, headline: getHeadline(uc) })
  );
  const listName = `${domainIntro.title} Use Cases`;
  const domainFaqs = buildDomainFaqs(domainIntro, useCasesSeo);

  return {
    useCasesSeo,
    domainFaqs,
    itemListJsonLd: buildItemListJsonLd(useCasesSeo, listName),
    faqJsonLd: buildCombinedFaqJsonLd(domainFaqs, useCasesSeo),
    breadcrumbJsonLd: buildBreadcrumbJsonLd(domainIntro, domainSlug),
    webPageJsonLd: buildWebPageJsonLd(domainIntro, useCasesSeo, listName, domainSlug),
  };
}

